import React, { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import ReactGA from 'react-ga'

const Modal = ({
  id,
  trigger,
  closeIcon,
  children,
  open,
  onClose = () => void (0),
  trackingName,
  closeOnDimmerClick
}) => {
  const modalRef = useRef()
  const [triggerOpen, setTriggerOpen] = useState(false)
  const triggerElement = trigger ? React.cloneElement(trigger, {
    'data-testid': 'modal-trigger',
    onClick: () => setTriggerOpen(true)
  }) : <></>

  useEffect(() => {
    if (trackingName && (open || triggerOpen)) {
      ReactGA.pageview(trackingName)
    }
  }, [open, triggerOpen, trackingName])

  const closeModal = () => {
    setTriggerOpen(false)
    onClose()
  }

  useEffect(() => {
    if (closeOnDimmerClick || !closeIcon) {
      const handler = (e) => {
        if (open || triggerOpen) {
          if (!modalRef.current || modalRef.current.contains(e.target)) {
            return
          }
          setTriggerOpen(false)

          /**
           * Wait to clear form until modal transition is complete to avoid a minor glitching effect.
           *  -> transition reference: @moodxd/component-overlay/lib/overlay.css:18,37
           */
          setTimeout(() => {
            onClose()
          }, 250)
        }
      }
      document.addEventListener('mousedown', handler, true)
      document.addEventListener('touchstart', handler, true)
      return () => {
        document.removeEventListener('mousedown', handler)
      }
    }
    // eslint-disable-next-line
  }, [open, triggerOpen, closeOnDimmerClick])

  return <>
    {triggerElement}
    <div data-testid={id} id={id} className={`Overlay${(open || triggerOpen) ? ' is-open' : ''}`}
         role='dialog'>
      <div ref={modalRef} className='Overlay-body Container u-pA10'>
        {closeIcon && <div
          role='presentation'
          onClick={closeModal}
          className='Overlay-close u-link'
          id='close-link'
        >
          Close
        </div>}
        {children}
      </div>
    </div>
  </>
}

Modal.propTypes = {
  /** A unique DOM Element ID */
  id: PropTypes.string,
  /** A modal can have a trigger element to render, that opens the modal when clicked */
  trigger: PropTypes.node,
  /** The modal's main content to render */
  children: PropTypes.node,
  open: PropTypes.bool,
  /** A modal can call a provided callback function upon modal closing */
  onClose: PropTypes.func,
  /** A modal can have a specific name string to call analytics tracking when modal is opened. */
  trackingName: PropTypes.string,
  /** A modal can show a 'Close' icon in the upper-right corner of the modal */
  closeIcon: PropTypes.bool,
  /** A modal can close on user clicking outside of the modal */
  closeOnDimmerClick: PropTypes.bool
}

export default Modal